/**
 * Parent controls: the family code, who is in the family, the devices, and
 * the way out.
 *
 * They share a screen because they share a question -- who can see this
 * family, and how do I stop them. A durable shared code makes that question
 * sharper than it was, and the answer is the three controls a house key has:
 * see the key, change the locks, and take somebody's copy back.
 */

import { Loading } from '../components/Spinner';
import { useCallback, useEffect, useState } from 'react';
import { currentAccount, enablePin, noteAccountDeleted, signedInEmail } from '../auth/cognito';
import {
  MAX_ATTEMPTS,
  PIN_LENGTH,
  forgetPin,
  hasPin,
  isTooObvious,
  isWellFormed,
} from '../auth/pin';
import { PinEntry } from '../components/PinEntry';
import {
  approveDevice,
  deleteAccount,
  deletionPlan,
  denyDevice,
  listDevices,
  revokeDevice,
  type DeletionPlan,
  type Device,
} from '../family/devices';
import {
  formatFamilyCode,
  joinLink,
  listMembers,
  readFamilyCode,
  removeMember,
  rotateFamilyCode,
  sealNameUnderCode,
  type Member,
} from '../family/families';
import { saveScene, savePerson, saveSettings } from '../family/state';
import { currentScene, DEFAULT_SCENE, SCENES } from '../valley/scene';
import { assetUrl } from '../valley/sprites';
import { Secret } from '../components/Secret';
import { ShareActions } from './ShareActions';
import { remindMember } from '../family/memberDevices';
import { answerKeyRequest, findKeyRequest, type KeyRequest } from '../family/keyRequests';
import { BackupSection } from './BackupSection';
import { Units } from './Units';
import { currentUnit } from '../family/money';
import { ChildCode } from './ChildCode';
import { ChildPrivacy } from './ChildPrivacy';
import type { Person } from '../family/state';
import { recoverySaved } from './RecoverySheet';
import { BackButton } from '../components/BackButton';
import { CloseButton } from '../components/CloseButton';
import { useDismissed } from '../family/dismissed';
import { SETTINGS_ICONS } from '../brand/settingsIcons';

export function Settings({
  familyId,
  familyKey,
  familyName,
  people,
  focus,
  onBack,
  onRenamed,
  onRestored,
  onUnitChanged,
  onSignOut,
  onForgetDevice,
  onSwitchAccount,
}: {
  familyId: string;
  familyKey: CryptoKey;
  familyName: string;
  people: Person[];
  /** Which section to scroll to, when Home deep-linked into here. */
  focus?: string | null;
  onBack?: () => void;
  onRenamed: (name: string) => void;
  /** A backup was restored into this family: read it again. */
  onRestored?: () => void;
  /** The unit changed: read the family again, so everything shows in it. */
  onUnitChanged?: () => void;
  onSignOut: () => void;
  onForgetDevice: () => void;
  /** Lock this account and show the account picker. */
  onSwitchAccount?: () => void;
}) {
  const { isDismissed, dismiss } = useDismissed(familyId);
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setDevices(await listDevices(familyId, familyKey));
  }, [familyId, familyKey]);

  useEffect(() => {
    void refresh().catch((problem: unknown) => {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    });
  }, [refresh]);

  /**
   * WHICH PART IS OPEN (Living Valley E2/E3). Settings is a list of rows, and
   * a row opens its part: in place of the list on a phone, beside it on a
   * wide screen. Every part is still rendered -- only shown or hidden -- so
   * nothing that was reachable before has moved out of the page, and Home's
   * "go to Settings, at X" links (`focus`) open X directly.
   */
  const [open, setOpen] = useState<string | null>(focus ?? null);
  useEffect(() => {
    if (focus) setOpen(focus);
  }, [focus]);

  useEffect(() => {
    if (!open) return;
    // After the first paint, so the section exists to scroll to.
    const timer = window.setTimeout(() => {
      const el = document.getElementById(open);
      if (el && window.innerWidth < 1024) el.closest('.settings-detail')?.scrollIntoView({ block: 'start' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  async function attempt(work: () => Promise<void>): Promise<void> {
    setError('');
    setBusy(true);
    try {
      await work();
      await refresh();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  // Displays only. Everything scoped to a person is shown under that person,
  // where "which of Ravi's devices are these" is answerable.
  const walls = (devices ?? []).filter((device) => device.scope === 'display');
  const live = walls.filter((device) => !device.revoked);
  const gone = walls.filter((device) => device.revoked);

  return (
    <>
      <h2>Settings</h2>
      {!recoverySaved(familyId) && !isDismissed('recovery-sheet') ? (
        <div className="settings-reminder">
          <SettingsGlyph glyph="sheet" tone="debit" />
          <div>
            <p className="settings-reminder-title">Print your recovery sheet</p>
            <p className="settings-reminder-text">Without it, a lost family code can&rsquo;t be undone.</p>
          </div>
          <button type="button" className="ink" onClick={() => setOpen('backup')}>
            Print
          </button>
          <CloseButton label="Hide this reminder" onClick={() => dismiss('recovery-sheet')} />
        </div>
      ) : null}
      <div className={open ? 'settings-layout is-open' : 'settings-layout'}>
        <nav className="settings-list" aria-label="Settings">
          {SETTINGS_GROUPS.map((group) => (
            <div className="settings-group" key={group.title}>
              <p className="settings-group-title">{group.title}</p>
              <ul>
                {group.rows.map((row) => {
                  const value =
                    row.id === 'family-details'
                      ? familyName || ''
                      : row.id === 'units'
                        ? unitWords()
                        : row.id === 'backup'
                          ? recoverySaved(familyId)
                            ? ''
                            : 'Not printed'
                          : row.id === 'people-devices'
                            ? people.map((p) => p.name).filter(Boolean).join(', ')
                            : '';
                  return (
                    <li key={row.id}>
                      <a
                        href={`#${row.id}`}
                        className={(open ?? 'family-details') === row.id ? 'settings-row on' : 'settings-row'}
                        aria-current={open === row.id ? 'true' : undefined}
                        onClick={(event) => {
                          event.preventDefault();
                          setOpen(row.id);
                        }}
                      >
                        <SettingsGlyph glyph={row.glyph} tone={row.tone} />
                        <span className="settings-row-label">{row.label}</span>
                        <span className={value === 'Not printed' ? 'settings-row-value unsafe' : 'settings-row-value'}>
                          {value}
                        </span>
                        <svg className="chev" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
        <div className="settings-detail" data-show={open ?? 'family-details'}>
          <BackButton label="All settings" className="settings-back" onClick={() => setOpen(null)} />
      {error ? <p className="error">{error}</p> : null}

      <FamilyCode familyId={familyId} familyKey={familyKey} familyName={familyName} />

      <BackupSection
        familyId={familyId}
        familyKey={familyKey}
        familyName={familyName}
        people={people}
        onRestored={() => onRestored?.()}
      />

      <Members familyId={familyId} familyKey={familyKey} />

      <FamilyDetails
        familyId={familyId}
        familyKey={familyKey}
        familyName={familyName}
        people={people}
        onRenamed={onRenamed}
      />

      {/* Read the family again after a change, as for the unit, so every
          screen behind Settings repaints in the new valley. */}
      <ValleyScenes familyId={familyId} onSaved={() => onUnitChanged?.()} />

      <Units
        familyId={familyId}
        familyKey={familyKey}
        people={people}
        unit={currentUnit()}
        onSaved={() => onUnitChanged?.()}
      />

      <PeopleDevices
        familyId={familyId}
        familyKey={familyKey}
        people={people}
        devices={devices}
        busy={busy}
        onChanged={(work) => void attempt(work)}
        onError={setError}
      />

      <section id="wall-displays">
      <p className="field-label">The valley on a screen</p>
      <p className="footnote">
        A tablet in the kitchen, an old laptop in the hall: that screen shows
        the valley all day, and it is simply <strong>your own account</strong>,
        open on it. There is no separate code to link.
      </p>
      <ol className="wall-steps">
        <li>On that screen, open Birbly and sign in as yourself.</li>
        <li>Set a PIN (below, under PIN) if you have not &mdash; it is what keeps the account behind the valley.</li>
        <li>On Home, tap <strong>Show the valley on this screen</strong>.</li>
      </ol>
      <p className="footnote">
        It stays up and changes as soon as anything is saved from any other
        device &mdash; approve a look, record money or complete a goal from
        your phone and the screen follows. Tapping the sun or moon asks for the
        PIN before it shows anything else, and tapping a child shows their
        card.
      </p>

      {live.length ? (
        <>
          <p className="field-label">Linked the old way</p>
          <p className="footnote">
            These were linked with a display code, which the web app no longer
            makes. They keep working until you unlink them.
          </p>
          {live.map((device) => (
            <div className="person" key={device.id}>
              <div>
                <p className="person-name">{device.label || 'Unnamed device'}</p>
                <p className="person-money">
                  Wall display
                  {device.lastSeenDate ? ` · last seen ${device.lastSeenDate}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="text-button"
                disabled={busy}
                onClick={() => void attempt(() => revokeDevice(familyId, device.id))}
              >
                Unlink
              </button>
            </div>
          ))}
        </>
      ) : null}

      {gone.length ? (
        <>
          <p className="field-label">Unlinked</p>
          {gone.map((device) => (
            <div className="entry voided" key={device.id}>
              <div>
                <p className="entry-reason">{device.label || 'Unnamed device'}</p>
                <p className="entry-date">No longer has access</p>
              </div>
            </div>
          ))}
          <p className="footnote">
            Unlinking takes effect at once, with one caveat worth knowing: the
            gateway caches its answer for up to five minutes, so a device can
            keep working for that long.
          </p>
        </>
      ) : null}

      </section>

      <QuickAccess />

      <section id="this-device">
        <ThisDevice onForget={onForgetDevice} onSignOut={onSignOut} {...(onSwitchAccount ? { onSwitch: onSwitchAccount } : {})} />
      </section>

      <section id="delete-account">
        <DangerZone familyName={familyName} onSignOut={onSignOut} />
      </section>
        </div>
      </div>

      {onBack ? (
        <div className="actions">
          <button type="button" onClick={onBack}>
            Back
          </button>
        </div>
      ) : null}
    </>
  );
}

/**
 * The family code: show it, send it, change it.
 *
 * READ BACK, not remembered. The code comes from `codeCt` on the family's own
 * settings -- the current code, encrypted under the family key -- so any
 * unlocked device can show it, including one belonging to a parent who was
 * not there when it was last rotated. That is what makes "what's our family
 * code?" a question with an answer, and it is the whole difference between
 * this and the phrase it replaced.
 *
 * It adds no exposure over what this screen already has: reading `codeCt`
 * needs a membership to fetch it and the family key to open it, and anybody
 * holding both already reads every name in the family. The server holds only
 * `sha256` of the code and could not show it to anybody.
 */
function FamilyCode({
  familyId,
  familyKey,
  familyName,
}: {
  familyId: string;
  familyKey: CryptoKey;
  familyName: string;
}) {
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void readFamilyCode(familyId, familyKey)
      .then((found) => {
        if (!cancelled) setCode(found);
      })
      .catch(() => {
        if (!cancelled) setCode(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [familyId, familyKey]);

  function rotate(): void {
    setWorking(true);
    setError('');
    void rotateFamilyCode(familyId, familyKey, familyName)
      .then((fresh) => {
        setCode(fresh);
        setConfirming(false);
      })
      .catch((problem: unknown) =>
        setError(problem instanceof Error ? problem.message : 'Something went wrong.'),
      )
      .finally(() => setWorking(false));
  }

  return (
    <section id="family-code">
      <p className="field-label">Family code</p>

      {loading ? <Loading label="Reading the family code…" /> : null}

      {!loading && !code ? (
        <>
          <p className="footnote">
            This family has no code on this device yet. Making one lets another
            parent join, and lets you unlock your family on a new device.
          </p>
          <button aria-busy={working}
            type="button"
            className="text-button"
            disabled={working}
            onClick={rotate}
          >
            {working ? 'Working…' : 'Make a family code'}
          </button>
        </>
      ) : null}

      {code ? (
        <>
          <Secret value={formatFamilyCode(code)} />
          <ShareActions code={code} familyName={familyName} link={joinLink(code)} />
          <p className="footnote">
            One code for the whole family. Anybody who has it can join and read
            everyone&rsquo;s names, so send it the way you would send a door
            key &mdash; and change it if it goes somewhere it should not.
          </p>

          {confirming ? (
            <div className="danger-box">
              <p>
                <strong>Change the family code?</strong>
              </p>
              <p>
                Every link you have already sent stops working, and anybody
                part-way through joining will need the new code.
              </p>
              <p>
                Devices that are already unlocked &mdash; including this one,
                and every other parent&rsquo;s &mdash; are not affected, and
                nobody is removed from the family. To take somebody&rsquo;s
                access away, remove them from Members below.
              </p>
              <div className="actions">
                <button aria-busy={working}
                  type="button"
                  className="danger-button"
                  disabled={working}
                  onClick={rotate}
                >
                  {working ? 'Changing…' : 'Change it'}
                </button>
                <button type="button" disabled={working} onClick={() => setConfirming(false)}>
                  Keep the current one
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="text-button danger"
              onClick={() => setConfirming(true)}
            >
              Change code
            </button>
          )}
        </>
      ) : null}

      {error ? <p className="error">{error}</p> : null}
    </section>
  );
}

/**
 * A parent's locked device, let in by the one-time code its screen shows.
 *
 * They tapped "Ask another device to let me in" and read the code out; it is
 * typed here, this screen says whose device it is, and a yes hands that
 * device the family key wrapped so only it can open it. The code is spent
 * by the answer. See family/keyRequests.ts.
 */
function LetDeviceIn({
  familyId,
  familyKey,
  onDone,
}: {
  familyId: string;
  familyKey: CryptoKey;
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [found, setFound] = useState<KeyRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const who = found
    ? found.email || `A parent who joined ${found.since?.slice(0, 10) ?? 'earlier'}`
    : '';

  return (
    <div className="let-in">
      <p className="field-label">Let a parent&rsquo;s device in</p>
      {found ? (
        <>
          <p>
            <strong>{who}</strong> is asking to unlock Birbly on their device. Let it in? It
            will read everything this one can.
          </p>
          <div className="actions">
            <button aria-busy={busy}
              type="button"
              className="primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await answerKeyRequest(familyId, familyKey, found, true);
                  setNote(`Done. ${who}'s device opens the next time they look at it.`);
                  setFound(null);
                  setCode('');
                  onDone();
                })
              }
            >
              {busy ? 'One moment…' : 'Let it in'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await answerKeyRequest(familyId, familyKey, found, false);
                  setNote('Refused. That code no longer works.');
                  setFound(null);
                  setCode('');
                })
              }
            >
              Not them
            </button>
          </div>
        </>
      ) : (
        <form
          className="let-in-form"
          onSubmit={(event) => {
            event.preventDefault();
            setNote('');
            void run(async () => setFound(await findKeyRequest(familyId, familyKey, code)));
          }}
        >
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="ABCD-1234"
            autoComplete="off"
            aria-label="Their one-time code"
          />
          <button aria-busy={busy} type="submit" disabled={busy || code.trim().length < 8}>
            {busy ? 'Looking…' : 'Find it'}
          </button>
        </form>
      )}
      <p className="footnote">
        When a parent taps &ldquo;Ask another device to let me in&rdquo;, their screen shows
        a one-time code. Type it here.
      </p>
      {note ? <p className="footnote">{note}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </div>
  );
}

/**
 * Who is in the family.
 *
 * A durable shared secret has to come with this. Without a list, "who can
 * read my family's names" has no answer; without removal, the only answer to
 * a wrong one is to rotate and hope.
 *
 * The email beside each row is that member's own, sealed under the family key
 * by their own device when they joined. The server has never held an address
 * it can read, and this list does not change that -- it decrypts here.
 */
function Members({ familyId, familyKey }: { familyId: string; familyKey: CryptoKey }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  /** Member id -> what happened when they were emailed. */
  const [reminded, setReminded] = useState<Record<string, string>>({});
  const me = currentAccount()?.sub;

  const refresh = useCallback(async () => {
    setMembers(await listMembers(familyId, familyKey));
  }, [familyId, familyKey]);

  useEffect(() => {
    void refresh().catch((problem: unknown) => {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    });
  }, [refresh]);

  return (
    <section id="members">
      <LetDeviceIn familyId={familyId} familyKey={familyKey} onDone={() => void refresh()} />

      <p className="field-label">Members</p>
      <p className="footnote">
        Everybody who has used your family code. They can each see the money,
        the goals, the journal and everyone&rsquo;s names.
      </p>

      {error ? <p className="error">{error}</p> : null}
      {!members ? <Loading label="Finding the grown-ups…" /> : null}

      {(members ?? []).map((member) => (
        <div className="person" key={member.sub}>
          <div>
            <p className="person-name">{member.email || 'A parent'}</p>
            <p className="person-money">
              {member.since ? `Joined ${member.since.slice(0, 10)}` : 'In the family'}
              {member.email
                ? ''
                : ' · still in the family; their email shows again once they next open Birbly'}
            </p>
            {!member.email && member.sub !== me ? (
              reminded[member.sub] ? (
                <p className="footnote">{reminded[member.sub]}</p>
              ) : (
                <button
                  type="button"
                  className="text-button"
                  disabled={busy}
                  onClick={() => {
                    void remindMember(familyId, member.sub)
                      .then((sent) =>
                        setReminded((now) => ({
                          ...now,
                          [member.sub]: sent
                            ? 'Emailed. It tells them how to get back in, never the code.'
                            : 'That email could not be sent yet. Send them the code yourself.',
                        })),
                      )
                      .catch((problem: unknown) =>
                        setReminded((now) => ({
                          ...now,
                          [member.sub]: problem instanceof Error ? problem.message : 'That did not work.',
                        })),
                      );
                  }}
                >
                  Email them how to get back in
                </button>
              )
            ) : null}
          </div>
          {removing === member.sub ? (
            <button aria-busy={busy}
              type="button"
              className="text-button danger"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setError('');
                void removeMember(familyId, member.sub)
                  .then(refresh)
                  .catch((problem: unknown) =>
                    setError(
                      problem instanceof Error ? problem.message : 'Something went wrong.',
                    ),
                  )
                  .finally(() => {
                    setBusy(false);
                    setRemoving(null);
                  });
              }}
            >
              {busy ? 'Removing…' : 'Really remove'}
            </button>
          ) : (
            <button
              type="button"
              className="text-button"
              disabled={busy}
              onClick={() => setRemoving(member.sub)}
            >
              Remove
            </button>
          )}
        </div>
      ))}

      <p className="footnote">
        Removing somebody takes effect on their very next request. It does not
        change the family code, and a device they have already unlocked can
        still read what it downloaded &mdash; so if the code has gone somewhere
        it should not, change it above as well. You cannot remove yourself
        here; that is what deleting your account does, further down.
      </p>
    </section>
  );
}

/**
 * The family's name.
 *
 * It used to be on the setup screen, which ran exactly once and then could
 * not be reached again -- so the name typed in a parent's first thirty
 * seconds was the name forever. The monthly deposit that sat beside it is an
 * allowance now, under Money (see Allowance.tsx).
 *
 * Renaming re-seals the family's name under the family CODE as well as under
 * the key, or a join link would keep announcing the old name to the one
 * person who cannot check it.
 */
function FamilyDetails({
  familyId,
  familyKey,
  familyName,
  people,
  onRenamed,
}: {
  familyId: string;
  familyKey: CryptoKey;
  familyName: string;
  people: Person[];
  onRenamed: (name: string) => void;
}) {
  const [name, setName] = useState(familyName);
  const [saved, setSaved] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const ready = name.trim() !== '' && !working;

  return (
    <section id="family-details" aria-busy={working}>
      <p className="field-label">Your family</p>

      <label className="field">
        <span>What is your family called?</span>
        <input
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          maxLength={50}
          autoComplete="off"
          placeholder="The Smiths"
        />
        <small>Encrypted before it leaves this device.</small>
      </label>

      <p className="footnote">
        The monthly deposit is now <strong>Allowance</strong>, under Money
        &mdash; per child or the same for everyone, weekly to monthly.
      </p>

      {error ? <p className="error">{error}</p> : null}
      {saved ? <p className="footnote">Saved.</p> : null}

      <div className="form-actions">
      <button
        type="button"
        className="secondary"
        disabled={working || name === familyName}
        onClick={() => {
          setName(familyName);
          setError('');
          setSaved(false);
        }}
      >
        Cancel
      </button>
      <button aria-busy={working}
        type="button"
        className="primary"
        disabled={!ready || name.trim() === familyName}
        onClick={() => {
          setWorking(true);
          setError('');
          const trimmed = name.trim();
          void readFamilyCode(familyId, familyKey)
            .then(async (code) =>
              saveSettings(
                familyId,
                familyKey,
                {
                  name: trimmed,
                  ...(code ? { preview: await sealNameUnderCode(trimmed, code) } : {}),
                },
                people,
              ),
            )
            .then(() => {
              onRenamed(trimmed);
              setSaved(true);
            })
            .catch((problem: unknown) =>
              setError(problem instanceof Error ? problem.message : 'Something went wrong.'),
            )
            .finally(() => setWorking(false));
        }}
      >
        {working ? 'Saving…' : 'Save'}
      </button>
      </div>
    </section>
  );
}

/**
 * WHICH VALLEY (2026-10-18): the backgrounds in content/valley.toml
 * [[scene]]. Hidden while there is only one -- a choice of one is not a
 * setting. Part of "Your family" because it is the family's: the wall, the
 * parents' phones and every child's device all paint the one chosen here.
 */
export function ValleyScenes({ familyId, onSaved }: { familyId: string; onSaved?: () => void }) {
  const [chosen, setChosen] = useState(currentScene());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  if (SCENES.length < 2) return null;

  return (
    <section id="valley-scene" aria-busy={working}>
      <p className="field-label">Valley background</p>
      <div className="scene-choices" role="radiogroup" aria-label="Valley background">
        {SCENES.map((scene) => (
          <button
            key={scene.id}
            type="button"
            role="radio"
            aria-checked={chosen === scene.id}
            className={chosen === scene.id ? 'scene-choice on' : 'scene-choice'}
            disabled={working}
            onClick={() => {
              if (scene.id === chosen) return;
              const before = chosen;
              setChosen(scene.id);
              setWorking(true);
              setError('');
              void saveScene(familyId, scene.id === DEFAULT_SCENE ? null : scene.id)
                .then(() => onSaved?.())
                .catch((problem: unknown) => {
                  setChosen(before);
                  setError(problem instanceof Error ? problem.message : 'Something went wrong.');
                })
                .finally(() => setWorking(false));
            }}
          >
            {scene.swatch ? (
              <img src={assetUrl(scene.swatch, 'webp')} alt="" loading="lazy" decoding="async" draggable={false} />
            ) : null}
            <span>{scene.label}</span>
          </button>
        ))}
      </div>
      <small>Everyone in the family sees the same valley, on every device.</small>
      {error ? <p className="error">{error}</p> : null}
    </section>
  );
}

/**
 * Each child: their code, and the devices that have asked to be theirs.
 *
 * Under the PERSON rather than in a flat device list, because "which of Ravi's
 * devices are these" is the question a parent is actually asking, and a single
 * list of six devices belonging to two children does not answer it.
 *
 * Pending first, always. A request is the only thing on this screen that is
 * waiting on the parent, and it disappears by itself within a day if they
 * never come -- so it has to be the thing they see.
 */
function PeopleDevices({
  familyId,
  familyKey,
  people,
  devices,
  busy,
  onChanged,
  onError,
}: {
  familyId: string;
  familyKey: CryptoKey;
  people: Person[];
  devices: Device[] | null;
  busy: boolean;
  onChanged: (work: () => Promise<void>) => void;
  onError: (message: string) => void;
}) {
  return (
    <section id="people-devices">
      <p className="field-label">Devices and codes</p>
      <p className="footnote">
        Each person has one code, and it keeps working. They type it on any
        device they want to use, that device shows a picture and four
        characters, and you check those match before you say yes. If a code
        gets out, make a new one &mdash; the old one stops at once.
      </p>

      {people.length === 0 ? (
        <p className="footnote">Nobody on the roster yet.</p>
      ) : null}

      {people.map((person) => (
        <PersonDevices
          key={person.id}
          familyId={familyId}
          familyKey={familyKey}
          person={person}
          devices={(devices ?? []).filter((device) => device.personId === person.id)}
          busy={busy}
          onChanged={onChanged}
          onError={onError}
        />
      ))}
    </section>
  );
}

function PersonDevices({
  familyId,
  familyKey,
  person,
  devices,
  busy,
  onChanged,
  onError,
}: {
  familyId: string;
  familyKey: CryptoKey;
  person: Person;
  devices: Device[];
  busy: boolean;
  onChanged: (work: () => Promise<void>) => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);

  /**
   * A person added before per-person keys existed has none, and approving a
   * device for them would fail at the last step -- after a child has typed a
   * code and a parent has checked a match code, which is the worst possible
   * moment to discover it.
   *
   * Saving their name mints one (`savePerson` treats a null key as a new
   * one), so opening this section quietly does it. Everything about them
   * carries on working either way; what this adds is that their device can
   * now be handed a key at all.
   */
  useEffect(() => {
    if (!open || person.key || !person.name) return;
    let cancelled = false;
    void savePerson(familyId, familyKey, person.name, person)
      .then(() => {
        if (!cancelled) onChanged(async () => {});
      })
      .catch(() => {
        // Not worth interrupting a parent over. Approving will say what is
        // wrong if they get that far, and opening this again retries.
      });
    return () => {
      cancelled = true;
    };
  }, [open, person, familyId, familyKey, onChanged]);

  const waiting = devices.filter((device) => device.status === 'pending' && !device.revoked);
  const live = devices.filter((device) => device.status === 'approved' && !device.revoked);
  // 'discarded' belongs here or it belongs nowhere. It matches none of the
  // three lists on its own, so a request set aside by approving a sibling
  // would simply vanish -- and the server keeps that row precisely so a parent
  // who approved one device and saw two disappear can see which two.
  const refused = devices.filter(
    (device) =>
      device.status === 'denied' || device.status === 'discarded' || device.revoked,
  );

  return (
    <div className="record">
      <div className="person">
        <div>
          <p className="person-name">{person.name || 'Unnamed'}</p>
          <p className="person-money">
            {waiting.length
              ? `${waiting.length} waiting for you`
              : `${live.length} device${live.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <button type="button" className="text-button" onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Open'}
        </button>
      </div>

      {/* Requests are shown whether or not the person is expanded. A parent
          should not have to go looking for the thing that is waiting. */}
      {waiting.map((device) => (
        <ApprovalCard
          key={device.id}
          familyId={familyId}
          familyKey={familyKey}
          device={device}
          person={person}
          alsoWaiting={waiting.length - 1}
          busy={busy}
          onChanged={onChanged}
          onError={onError}
        />
      ))}

      {open ? (
        <>
          <ChildCode familyId={familyId} familyKey={familyKey} person={person} />
          <ChildPrivacy familyId={familyId} personId={person.id} name={person.name} />

          {live.length ? <p className="field-label">Their devices</p> : null}
          {live.map((device) => (
            <div className="person" key={device.id}>
              <div>
                <p className="person-name">{device.label || KIND_LABEL[device.kind ?? ''] || 'A device'}</p>
                <p className="person-money">
                  {device.approvedAt ? `Approved ${device.approvedAt.slice(0, 10)}` : 'Approved'}
                  {device.lastSeenDate ? ` · last seen ${device.lastSeenDate}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="text-button"
                disabled={busy}
                onClick={() => onChanged(() => revokeDevice(familyId, device.id))}
              >
                Unlink
              </button>
            </div>
          ))}

          {refused.length ? (
            <>
              <p className="field-label">Not allowed in</p>
              {refused.map((device) => (
                <div className="entry voided" key={device.id}>
                  <div>
                    <p className="entry-reason">
                      {KIND_LABEL[device.kind ?? ''] || device.label || 'A device'}
                    </p>
                    <p className="entry-date">
                      {device.discardedAt
                        ? `Set aside ${device.discardedAt.slice(0, 10)} — another device used the code`
                        : device.deniedAt
                          ? `Refused ${device.deniedAt.slice(0, 10)}`
                          : 'No longer has access'}
                    </p>
                  </div>
                </div>
              ))}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  phone: 'A phone',
  tablet: 'A tablet',
  computer: 'A computer',
  other: 'A device',
};

/**
 * One waiting device, and the check that makes the whole thing safe.
 *
 * The match code is the point of this card. A parent comparing it against the
 * screen in the child's hands is the only thing standing between a leaked
 * eight-character code and somebody reading a child's rows -- so it is the
 * biggest thing here, and the copy asks for the comparison rather than
 * assuming it.
 */
function ApprovalCard({
  familyId,
  familyKey,
  device,
  person,
  alsoWaiting,
  busy,
  onChanged,
  onError,
}: {
  familyId: string;
  familyKey: CryptoKey;
  device: Device;
  person: Person;
  /** Other requests on this same person, which approving this one sets aside. */
  alsoWaiting: number;
  busy: boolean;
  onChanged: (work: () => Promise<void>) => void;
  onError: (message: string) => void;
}) {
  const [label, setLabel] = useState('');

  return (
    <div className="danger-box">
      <p>
        <strong>{KIND_LABEL[device.kind ?? ''] || 'A device'}</strong> is asking
        to be {person.name || 'theirs'}.
      </p>
      <p className="match-code">{device.match}</p>
      <p className="footnote">
        Check that picture and those four characters are <strong>exactly</strong>{' '}
        what is on their screen. If they are not, this is not the device in
        front of you &mdash; say no.
      </p>
      {/* When two devices ask within a minute of each other the match code is
          still the thing that tells them apart -- but a parent who has just
          watched a child type on one of them knows which is which by WHEN, and
          that is quicker to check than four characters. */}
      {device.created ? (
        <p className="footnote">Asked {askedAt(device.created)}.</p>
      ) : null}
      {alsoWaiting > 0 ? (
        <p className="footnote">
          <strong>
            {alsoWaiting === 1
              ? 'One other device is waiting'
              : `${alsoWaiting} other devices are waiting`}
          </strong>{' '}
          on this code. Saying yes here sets {alsoWaiting === 1 ? 'it' : 'them'}{' '}
          aside and uses the code up &mdash; for another device,
          give {person.name || 'them'} a new one.
        </p>
      ) : null}

      <label className="field">
        <span>What is it? (optional)</span>
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={40}
          autoComplete="off"
          placeholder="The kitchen tablet"
        />
        <small>Encrypted before it leaves this device.</small>
      </label>

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() =>
            onChanged(async () => {
              try {
                await approveDevice(
                  familyId,
                  familyKey,
                  device,
                  person,
                  label.trim() || KIND_LABEL[device.kind ?? ''] || 'A device',
                );
              } catch (problem) {
                onError(
                  problem instanceof Error ? problem.message : 'Something went wrong.',
                );
                throw problem;
              }
            })
          }
        >
          Approve
        </button>
        <button
          type="button"
          className="danger-button"
          disabled={busy}
          onClick={() => onChanged(() => denyDevice(familyId, device.id))}
        >
          Not mine
        </button>
      </div>
    </div>
  );
}

/**
 * Forgetting THIS device.
 *
 * Separate from signing out, which it did not used to be. Signed in is a
 * property of the session; unlocked is a property of the device. Ending the
 * first used to end the second, and a parent who signed out on their own phone
 * was locked out of every name in their family unless they still had a phrase
 * they were shown once.
 *
 * This is the case that behaviour was really for: a borrowed laptop, a machine
 * in a library, a computer being sold. Rare, deliberate, and worth its own
 * button rather than a side effect of the common one.
 */
/**
 * The lock PIN: getting back in without a password.
 *
 * Two things at once, and it is worth being honest with the parent about
 * both. Setting a PIN makes coming back QUICKER -- six digits instead of a
 * Cognito password. It also makes walking away SAFER, because it is what
 * lets the app lock itself after ten quiet minutes; without a PIN the only
 * available lock is a full sign-out, which is why the app does not do it.
 *
 * The copy says the second part out loud. A parent who reads this as pure
 * convenience will not understand why the screen went away while they were
 * making dinner, and an unexplained lock is how a feature gets turned off.
 */
function QuickAccess() {
  const account = currentAccount();
  const [on, setOn] = useState(() => (account ? hasPin(account.sub) : false));
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  function reset(): void {
    setPin('');
    setAgain('');
    setError('');
    setOpen(false);
  }

  async function save(): Promise<void> {
    setError('');
    if (!isWellFormed(pin)) {
      setError(`A PIN is ${PIN_LENGTH} digits.`);
      return;
    }
    if (isTooObvious(pin)) {
      // Refused rather than warned about. Five guesses are allowed, and
      // these are the first five anybody tries.
      setError('Please pick something less guessable than that.');
      return;
    }
    if (pin !== again) {
      setError('Those two do not match.');
      setAgain('');
      return;
    }
    setBusy(true);
    try {
      await enablePin(pin);
      setOn(true);
      setDone('Saved. This account can now be unlocked with that PIN.');
      reset();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!account) return null;

  return (
    <section id="quick-access">
      <p className="field-label">Quick access</p>

      {on && !open ? (
        <>
          <p className="footnote">
            A PIN is set for <strong>{account.email || 'this account'}</strong>.
            Birbly locks itself after about ten quiet minutes, and your PIN
            opens it again.
          </p>
          <div className="actions">
            <button type="button" onClick={() => setOpen(true)}>
              Change PIN
            </button>
            <button
              type="button"
              className="text-button danger"
              onClick={() => {
                forgetPin(account.sub);
                setOn(false);
                setDone('PIN removed. This account signs in with its password.');
              }}
            >
              Turn it off
            </button>
          </div>
        </>
      ) : null}

      {!on && !open ? (
        <>
          <p className="footnote">
            Set a {PIN_LENGTH}-digit PIN and you can get back into this account
            without your password &mdash; and Birbly will lock itself when it
            has been left alone for a while, which it cannot do today. Anyone
            who does not know the PIN still needs your password.
          </p>
          <button type="button" onClick={() => setOpen(true)}>
            Set a PIN
          </button>
        </>
      ) : null}

      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <PinEntry
            label={on ? 'New PIN' : 'PIN'}
            value={pin}
            onChange={setPin}
            autoFocus
            disabled={busy}
          />
          <p className="field-label">Again</p>
          <input
            className="pin-entry"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={PIN_LENGTH}
            disabled={busy}
            value={again}
            onChange={(event) =>
              setAgain(event.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))
            }
          />
          {error ? <p className="error">{error}</p> : null}
          <p className="footnote">
            {MAX_ATTEMPTS} wrong tries and this account goes back to needing
            its password. Forgetting it costs nothing else &mdash; sign in
            once and set the same {PIN_LENGTH} digits again if you like.
          </p>
          <div className="actions">
            <button aria-busy={busy} className="primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save PIN'}
            </button>
            <button type="button" disabled={busy} onClick={reset}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {done && !open ? <p className="footnote">{done}</p> : null}
    </section>
  );
}

function ThisDevice({ onForget, onSignOut, onSwitch }: { onForget: () => void; onSignOut: () => void; onSwitch?: () => void }) {
  const [sure, setSure] = useState(false);
  /**
   * In full here, because the header truncates it.
   *
   * Two parents sharing a browser is a supported arrangement -- signing out
   * deliberately leaves the family key behind -- so "which account is this"
   * is a real question with a real answer, and this is the screen where
   * somebody who does not like the answer can do something about it.
   */
  const account = signedInEmail();

  return (
    <>
      <p className="field-label">This device</p>
      {account ? (
        <p className="footnote">
          Signed in as <strong>{account}</strong>.
        </p>
      ) : null}
      {/* Plain signing out. It used to live at the bottom of Home, which no
          longer has a bottom row; "Switch" in the corner locks instead. */}
      {/* Switch moved here from the corner (2026-10-13): it locks this
          account and shows the picker, so another parent can come in. */}
      {onSwitch ? (
        <button type="button" className="full secondary" onClick={onSwitch}>
          Switch account
        </button>
      ) : null}
      <button type="button" className="full" onClick={onSignOut}>
        Sign out
      </button>
      {sure ? (
        <div className="danger-box">
          <p>
            This device will forget your family key and sign out. Nothing is
            deleted anywhere else &mdash; your family, your money and everyone
            else&rsquo;s devices are untouched.
          </p>
          <p>
            To use Birbly here again you will need your family code. Any other
            member can read it off their own Settings screen.
          </p>
          <div className="actions">
            <button type="button" className="danger-button" onClick={onForget}>
              Forget it and sign out
            </button>
            <button type="button" onClick={() => setSure(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="footnote">
            Signing out leaves your family key on this device, so you do not
            have to type the code again every time. On a borrowed or shared
            computer, clear it instead.
          </p>
          <button type="button" className="text-button danger" onClick={() => setSure(true)}>
            Forget my family key on this device
          </button>
        </>
      )}
    </>
  );
}

/**
 * Deleting the account.
 *
 * The confirmation is typing the family's NAME, and it has to happen here
 * because it cannot happen on the server: nameCt is ciphertext the API has no
 * key for. What the server checks instead is the thing a client cannot be
 * trusted on -- whether this really is the last parent of the family being
 * destroyed.
 */
function DangerZone({
  familyName,
  onSignOut,
}: {
  familyName: string;
  onSignOut: () => void;
}) {
  /**
   * One red button, then the consequences and a typed "delete" (2026-10-13).
   * The plan is still asked of the server first, because only it knows
   * whether this is the last parent -- and so whether the family goes too.
   */
  const [asking, setAsking] = useState(false);
  const [plan, setPlan] = useState<DeletionPlan | null>(null);
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  function cancel(): void {
    setAsking(false);
    setPlan(null);
    setTyped('');
    setError('');
  }

  if (!asking) {
    return (
      <>
        <p className="field-label">Deleting your account</p>
        <button
          type="button"
          className="danger-button full"
          onClick={() => {
            setAsking(true);
            setError('');
            void deletionPlan()
              .then(setPlan)
              .catch((problem: unknown) =>
                setError(problem instanceof Error ? problem.message : 'Something went wrong.'),
              );
          }}
        >
          Delete account
        </button>
      </>
    );
  }

  const destroying = (plan?.destroys.length ?? 0) > 0;
  const confirmed = typed.trim().toLowerCase() === 'delete';

  return (
    <div className="danger-box">
      <p className="field-label">Delete your account?</p>

      {!plan && !error ? <Loading label="Checking what this would remove…" /> : null}

      {plan?.leaves.length ? (
        <p>
          You would leave {plan.leaves.length === 1 ? 'a family' : `${plan.leaves.length} families`}{' '}
          that carries on without you. Nothing there is lost.
        </p>
      ) : null}

      {destroying ? (
        <p>
          You are the last parent of{' '}
          <strong>{familyName || 'your family'}</strong>. Deleting your account
          destroys it: the ledger, every goal, the journal and every name.
          Nobody can undo this and we cannot restore it, because we cannot
          read it.
        </p>
      ) : null}

      {plan ? (
        <label className="field">
          <span>
            Type <strong>delete</strong> to confirm
          </span>
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="delete"
          />
        </label>
      ) : null}

      {error ? <p className="error">{error}</p> : null}

      <div className="form-actions">
        <button type="button" className="secondary" disabled={working} onClick={cancel}>
          Cancel
        </button>
        <button aria-busy={working}
          type="button"
          className="danger-button"
          disabled={working || !plan || !confirmed}
          onClick={() => {
            if (!plan) return;
            setWorking(true);
            setError('');
            void deleteAccount(plan.destroys)
              // Signing out clears every key this device holds. Doing it after
              // the delete means a failure leaves the parent still able to see
              // what did not happen. The front page then says it is done.
              .then(() => {
                noteAccountDeleted();
                onSignOut();
              })
              .catch((problem: unknown) => {
                setError(problem instanceof Error ? problem.message : 'Something went wrong.');
                setWorking(false);
              });
          }}
        >
          {working ? 'Deleting…' : 'Confirm delete'}
        </button>
      </div>
    </div>
  );
}


/**
 * When a request arrived, in the form a parent standing in a room needs.
 *
 * The date is noise: a pending request lives for a day at most. What is worth
 * reading is the clock time, because "the one from 7:42" is how somebody who
 * just watched a child type tells two requests apart.
 */
function askedAt(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const time = when.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const today = new Date().toDateString() === when.toDateString();
  return today ? `at ${time}` : `${when.toLocaleDateString()} at ${time}`;
}

// ---------------------------------------------------------------------------
// The list of rows (Living Valley E2/E3). Glyph and tile colour per the table
// in src/brand/settingsIcons.ts.
// ---------------------------------------------------------------------------

type Tone = 'meadow' | 'sky' | 'sun' | 'debit' | 'ink';

const SETTINGS_GROUPS: ReadonlyArray<{
  title: string;
  rows: ReadonlyArray<{ id: string; label: string; glyph: keyof typeof SETTINGS_ICONS; tone: Tone }>;
}> = [
  {
    title: 'Family',
    rows: [
      { id: 'family-details', label: 'Your family', glyph: 'house', tone: 'meadow' },
      { id: 'members', label: 'Grown-ups', glyph: 'grownup', tone: 'sky' },
      { id: 'units', label: 'Counting in', glyph: 'coin', tone: 'sun' },
    ],
  },
  {
    title: 'Keeping it safe',
    rows: [
      { id: 'backup', label: 'Recovery & backup', glyph: 'sheet', tone: 'debit' },
      { id: 'family-code', label: 'Family code', glyph: 'key', tone: 'debit' },
    ],
  },
  {
    title: 'Screens and devices',
    rows: [
      { id: 'people-devices', label: 'Devices and codes', glyph: 'phone', tone: 'sky' },
      { id: 'wall-displays', label: 'The valley on a screen', glyph: 'screen', tone: 'sky' },
    ],
  },
  {
    title: 'This device',
    rows: [
      { id: 'quick-access', label: 'PIN', glyph: 'key', tone: 'ink' },
      { id: 'this-device', label: 'Sign out', glyph: 'signout', tone: 'ink' },
      { id: 'delete-account', label: 'Delete account', glyph: 'signout', tone: 'debit' },
    ],
  },
];

function SettingsGlyph({ glyph, tone }: { glyph: keyof typeof SETTINGS_ICONS; tone: Tone }) {
  return (
    <span className={`icon-tile tile-${tone}`} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: SETTINGS_ICONS[glyph] }}
      />
    </span>
  );
}

function unitWords(): string {
  const unit = currentUnit();
  if (unit.type === 'currency') return unit.currency === 'USD' ? 'Dollars' : unit.currency;
  if (unit.type === 'marbles') return 'Marbles';
  return unit.many.charAt(0).toUpperCase() + unit.many.slice(1);
}
